import { useEffect, useState } from 'react'
import {
  api,
  type AccountStagePositionsView,
  type QueueStageValue,
  type StagePositions,
} from '../lib/api.ts'
import { QUEUE_STAGES } from '../lib/queueStages.ts'
import { announceSaved } from '../lib/sessionStagePositions.ts'
import { STAGE_LABEL } from '../lib/vocabulary.ts'
import { useConfirm } from './Confirm.tsx'
import { DataTable, type Column } from './DataTable.tsx'
import { EntityForm } from './EntityForm.tsx'
import { Input } from './Input.tsx'
import { useToast } from './Toast.tsx'

/**
 * Where this account's stages start on its own FC12540 (#673), on the
 * account's page and saved apart from everything else there.
 *
 * The positions are the installation's: whoever sets them reads the list off
 * the pharmacy's ERP and types, for each of our stages, the position it starts
 * at. The server refuses them out of order; its reason is shown as it is.
 *
 * A table since #690, a row per stage, edited in the row: where each starts,
 * where that leaves it ending, and what the stage means for a verification.
 *
 * Saving asks first, naming the change: the next stage sweep reads every
 * requisition of the account on the new positions, and closes as released the
 * open verifications it then finds in production -- which nothing undoes.
 */
export function AccountStagePositions({ accountId }: { accountId: string }) {
  const [view, setView] = useState<AccountStagePositionsView | undefined>()
  const [form, setForm] = useState<StagePositionsForm | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [saving, setSaving] = useState(false)
  const toast = useToast()
  const confirm = useConfirm()

  function take(next: AccountStagePositionsView) {
    setView(next)
    setForm(formOf(next.positions))
  }

  useEffect(() => {
    void api
      .accountStagePositions(accountId)
      .then(take)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'falha ao carregar'),
      )
  }, [accountId])

  if (error !== undefined) return <p className="text-tP text-text-error">{error}</p>
  if (view === undefined || form === undefined) return null

  const dirty = JSON.stringify(form) !== JSON.stringify(formOf(view.positions))

  async function save() {
    if (form === undefined || view === undefined) return
    const positions = positionsOf(form)
    const ok = await confirm({
      title: 'Salvar as posições das etapas?',
      message: saveWarning(view.positions, positions),
      confirmLabel: 'Salvar',
      tone: 'danger',
    })
    if (!ok) return
    setSaving(true)
    try {
      const saved = await api.saveAccountStagePositions(accountId, positions)
      take(saved)
      announceSaved({ accountId, positions: saved.positions })
      toast.show('Posições das etapas salvas', { tone: 'success' })
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao salvar', { tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <EntityForm
      title="Etapas da bancada no ERP"
      description={<StagePositionsHelp view={view} />}
      onSave={() => void save()}
      saving={saving}
      dirty={dirty}
    >
      <div className="md:col-span-2">
        <StagePositionTable form={form} onChange={setForm} />
      </div>
    </EntityForm>
  )
}

/** What the page's form holds: the positions as typed, digits only. */
export type StagePositionsForm = Readonly<Record<QueueStageValue, string>>

export function formOf(positions: StagePositions): StagePositionsForm {
  return Object.fromEntries(
    QUEUE_STAGES.map((stage) => [stage, String(positions[stage])]),
  ) as StagePositionsForm
}

/** What is sent. An emptied field goes as 0, which the server refuses with its reason. */
export function positionsOf(form: StagePositionsForm): StagePositions {
  return Object.fromEntries(
    QUEUE_STAGES.map((stage) => [stage, Number(form[stage]) || 0]),
  ) as StagePositions
}

/** The positions in the bench's order, as a sentence lists them. */
function listed(positions: StagePositions): string {
  return QUEUE_STAGES.map((stage) => `${STAGE_LABEL[stage]} ${positions[stage]}`).join(', ')
}

/** What saving changes, said before it is done. */
export function saveWarning(before: StagePositions, after: StagePositions): string {
  return (
    `De ${listed(before)} para ${listed(after)}. Na próxima leitura das etapas, cada ` +
    'requisição desta conta passa para a etapa dessas posições, e as conferências em aberto que ' +
    'ficarem na produção são encerradas como liberadas, o que não se desfaz.'
  )
}

/**
 * Where each stage ends on the positions as typed: one before the next one
 * starts, and the last runs on. "—" where the next start is missing or not
 * past this one's, which the server will refuse anyway.
 */
export function stageEnds(form: StagePositionsForm): Readonly<Record<QueueStageValue, string>> {
  return Object.fromEntries(
    QUEUE_STAGES.map((stage, i) => {
      const next = QUEUE_STAGES[i + 1]
      if (next === undefined) return [stage, 'em diante']
      const from = Number(form[stage])
      const nextFrom = Number(form[next])
      return [stage, form[next] === '' || !(nextFrom > from) ? '—' : String(nextFrom - 1)]
    }),
  ) as Record<QueueStageValue, string>
}

/** What each stage means for a verification, beside where it starts. */
const STAGE_NOTE: Readonly<Record<QueueStageValue, string>> = {
  comercial:
    'Antes da conferência, inclusive a requisição que ainda não andou. A posição só confere a ordem: tudo abaixo da conferência já é comercial.',
  conferencia: 'Na bancada, esperando a conferência.',
  producao: 'Já fabricada ou entregue: a conferência é encerrada como liberada.',
}

/** A row per stage, in the bench's order, its start edited in the row. */
export function StagePositionTable({
  form,
  onChange,
}: {
  form: StagePositionsForm
  onChange: (next: StagePositionsForm) => void
}) {
  const ends = stageEnds(form)
  const columns: ReadonlyArray<Column<QueueStageValue>> = [
    {
      key: 'stage',
      header: 'Etapa',
      className: 'text-tPSemi whitespace-nowrap',
      render: (stage) => STAGE_LABEL[stage],
    },
    {
      key: 'start',
      header: 'Começa na posição',
      render: (stage) => (
        <Input
          aria-label={`${STAGE_LABEL[stage]}: começa na posição`}
          size="small"
          inputMode="numeric"
          value={form[stage]}
          containerClassName="w-24"
          onChange={(event) =>
            onChange({ ...form, [stage]: event.target.value.replace(/\D/g, '') })
          }
        />
      ),
    },
    {
      key: 'end',
      header: 'Vai até',
      className: 'tabular-nums text-text-weak whitespace-nowrap',
      render: (stage) => ends[stage],
    },
    {
      key: 'means',
      header: 'O que é',
      className: 'text-tSmall text-text-weak',
      render: (stage) => STAGE_NOTE[stage],
    },
  ]
  return <DataTable columns={columns} rows={QUEUE_STAGES} rowKey={(stage) => stage} pageSize={3} />
}

/** How the positions are read, and whose they are. */
export function StagePositionsHelp({ view }: { view: AccountStagePositionsView }) {
  const defaults = QUEUE_STAGES.map((stage) => view.defaults[stage]).join(', ')
  return (
    <>
      A posição do FC12540 em que cada etapa começa nesta instalação. A lista da instalação sai de{' '}
      <code>SELECT POSICAO, DESCRICAO FROM FC12540</code>.{' '}
      {view.setBy === null || view.setAt === null
        ? `Nada salvo ainda: vale o padrão (${defaults}).`
        : `Salvas por ${view.setBy} em ${new Date(view.setAt).toLocaleString('pt-BR', {
            dateStyle: 'short',
            timeStyle: 'short',
          })}.`}
    </>
  )
}
